import { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Navigation, Share2, QrCode, Pencil, Trash2, Star, Copy, MessageCircle, Phone, Crown, MapPin,
} from 'lucide-react';
import {
  getType, localisation, formatCoords, ouvrirNavigation, partager, partagerWhatsApp, copier, NAVIGATEURS,
} from '../lib/lieux';
import { useLieux } from '../hooks/useLieux';
import { useToast, ConfirmModal } from './ui';
import QRModal from './QRModal';

export const TypeBadge = ({ type, petit }) => {
  const t = getType(type);
  const Icone = t.icon;
  return (
    <span className={`type-badge ${petit ? 'type-badge-petit' : ''}`}>
      <Icone size={petit ? 13 : 14} aria-hidden="true" />
      {t.label}
    </span>
  );
};

export const TypeIcone = ({ type, size = 20 }) => {
  const Icone = getType(type).icon;
  return <span className="type-icone"><Icone size={size} aria-hidden="true" /></span>;
};

// ───────────────────────── Carte d'un lieu (liste)
export const LieuCard = memo(function LieuCard({ lieu, actif, onOuvrir, onFavori, onQR }) {
  const toast = useToast();

  const surPartager = async (e) => {
    e.stopPropagation();
    const r = await partager(lieu);
    if (r === 'copie') toast('Lien copié');
  };

  return (
    <article className={`carte-lieu ${actif ? 'carte-lieu-active' : ''}`}>
      <button className="carte-lieu-zone" onClick={() => onOuvrir(lieu)} aria-label={`Voir ${lieu.nom}`}>
        <div className="carte-lieu-tete">
          <TypeBadge type={lieu.type} />
          {lieu.principal && <span className="badge-principal"><Crown size={12} /> Principal</span>}
        </div>
        <h3 className="carte-lieu-nom">{lieu.nom || lieu.code_cau}</h3>
        <div className="cau">{lieu.code_cau}</div>
        {localisation(lieu) && <p className="carte-lieu-loc"><MapPin size={14} aria-hidden="true" /> {localisation(lieu)}</p>}
      </button>

      <button
        className={`btn-favori ${lieu.favori ? 'actif' : ''}`}
        onClick={(e) => { e.stopPropagation(); onFavori(lieu); }}
        aria-label={lieu.favori ? 'Retirer des favoris' : 'Ajouter aux favoris'}
        aria-pressed={lieu.favori}
      >
        <Star size={20} fill={lieu.favori ? 'currentColor' : 'none'} />
      </button>

      <div className="carte-lieu-actions">
        <button className="btn btn-primaire btn-aller" onClick={() => ouvrirNavigation(lieu)}>
          <Navigation size={18} /> Y aller
        </button>
        <button className="btn-icone btn-icone-bord" onClick={surPartager} aria-label="Partager"><Share2 size={19} /></button>
        <button className="btn-icone btn-icone-bord" onClick={(e) => { e.stopPropagation(); onQR(lieu); }} aria-label="QR Code"><QrCode size={19} /></button>
      </div>
    </article>
  );
});

// ───────────────────────── Détail d'un lieu (bottom sheet mobile / panneau desktop)
export const LieuDetail = ({ lieu, onFerme }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { basculerFavori, definirPrincipal, supprimer } = useLieux();
  const [qr, setQr] = useState(false);
  const [confirmer, setConfirmer] = useState(false);

  if (!lieu) return null;

  const copierCAU = async () => { if (await copier(lieu.code_cau)) toast('CAU copié'); };
  const copierGPS = async () => { if (await copier(formatCoords(lieu))) toast('Coordonnées copiées'); };
  const surPartager = async () => { const r = await partager(lieu); if (r === 'copie') toast('Lien copié'); };
  const surFavori = async () => {
    const r = await basculerFavori(lieu);
    if (r.error) toast(r.error, 'erreur');
    else toast(lieu.favori ? 'Retiré des favoris' : 'Ajouté aux favoris');
  };
  const surPrincipal = async () => {
    const r = await definirPrincipal(lieu);
    if (r.error) toast(r.error, 'erreur'); else toast('Lieu principal défini');
  };
  const surSupprimer = async () => {
    const r = await supprimer(lieu.id);
    if (r.error) { toast(r.error, 'erreur'); return; }
    setConfirmer(false);
    toast('Lieu supprimé');
    onFerme && onFerme();
  };

  return (
    <div className="detail">
      <div className="detail-tete">
        <TypeBadge type={lieu.type} />
        {lieu.principal && <span className="badge-principal"><Crown size={12} /> Principal</span>}
        <button className={`btn-favori btn-favori-inline ${lieu.favori ? 'actif' : ''}`} onClick={surFavori}
          aria-label={lieu.favori ? 'Retirer des favoris' : 'Ajouter aux favoris'} aria-pressed={lieu.favori}>
          <Star size={22} fill={lieu.favori ? 'currentColor' : 'none'} />
        </button>
      </div>

      <h2 className="detail-nom">{lieu.nom || lieu.code_cau}</h2>

      <button className="cau cau-grand cau-copiable" onClick={copierCAU} aria-label="Copier le CAU">
        {lieu.code_cau} <Copy size={16} />
      </button>
      <p className="statut-actif"><span className="point" /> Adresse active</p>

      {localisation(lieu) && <p className="detail-loc"><MapPin size={16} /> {[lieu.quartier, lieu.commune !== lieu.quartier ? lieu.commune : null, lieu.ville].filter(Boolean).join(' · ')}</p>}
      <button className="detail-gps" onClick={copierGPS} aria-label="Copier les coordonnées GPS">
        <span>GPS</span> {formatCoords(lieu)} <Copy size={14} />
      </button>

      <button className="btn btn-primaire btn-large btn-aller" onClick={() => ouvrirNavigation(lieu, 'google')}>
        <Navigation size={20} /> Y aller
      </button>
      <div className="detail-navs">
        {NAVIGATEURS.map(n => (
          <button key={n.id} className="btn btn-secondaire" onClick={() => ouvrirNavigation(lieu, n.id)}>{n.label}</button>
        ))}
      </div>

      <div className="detail-actions">
        <button className="action" onClick={surPartager}><Share2 size={20} /><span>Partager</span></button>
        <button className="action" onClick={() => partagerWhatsApp(lieu)}><MessageCircle size={20} /><span>WhatsApp</span></button>
        <button className="action" onClick={() => setQr(true)}><QrCode size={20} /><span>QR Code</span></button>
        <button className="action" onClick={() => navigate(`/lieux/${lieu.id}/modifier`)}><Pencil size={20} /><span>Modifier</span></button>
      </div>

      {(lieu.description || lieu.note || lieu.telephone || (lieu.alias && lieu.alias.length > 0)) && (
        <div className="detail-infos">
          {lieu.description && <div><h4>Description</h4><p>{lieu.description}</p></div>}
          {lieu.alias && lieu.alias.length > 0 && (
            <div><h4>Aussi appelé</h4><div className="alias-liste">{lieu.alias.map(a => <span key={a} className="alias">{a}</span>)}</div></div>
          )}
          {lieu.telephone && <div><h4>Téléphone</h4><a className="lien" href={`tel:${lieu.telephone}`}><Phone size={14} /> {lieu.telephone}</a></div>}
          {lieu.note && <div><h4>Notes privées</h4><p className="texte-secondaire">{lieu.note}</p></div>}
        </div>
      )}

      <div className="detail-pied">
        {!lieu.principal && (
          <button className="btn btn-fantome" onClick={surPrincipal}><Crown size={16} /> Définir comme lieu principal</button>
        )}
        <button className="btn btn-fantome btn-fantome-danger" onClick={() => setConfirmer(true)}><Trash2 size={16} /> Supprimer</button>
      </div>

      <QRModal lieu={qr ? lieu : null} onClose={() => setQr(false)} />
      <ConfirmModal
        open={confirmer}
        titre="Supprimer ce lieu ?"
        code={lieu.code_cau}
        message="Cette action est irréversible. Les liens et QR Codes déjà partagés ne fonctionneront plus."
        onConfirm={surSupprimer}
        onClose={() => setConfirmer(false)}
      />
    </div>
  );
};
